import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Trash2, Image, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [formData, setFormData] = useState({
    title: "",
    type: "photo",
    url: "",
    thumbnail_url: "",
    description: "",
    tags: ""
  });

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=100`);
      setMedia(response.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const filteredMedia = filterType === "all" 
    ? media 
    : media.filter(m => m.type === filterType);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };

      await axios.post(`${API}/media`, payload, { withCredentials: true });
      toast.success("Media added successfully");
      
      setIsDialogOpen(false);
      resetForm();
      fetchMedia();
    } catch (error) {
      console.error("Error saving media:", error);
      toast.error("Failed to save media");
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media item?")) return;
    
    try {
      await axios.delete(`${API}/media/${mediaId}`, { withCredentials: true });
      toast.success("Media deleted successfully");
      fetchMedia();
    } catch (error) {
      console.error("Error deleting media:", error);
      toast.error("Failed to delete media");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      type: "photo",
      url: "",
      thumbnail_url: "",
      description: "",
      tags: ""
    });
  };

  return (
    <AdminLayout title="Media">
      <div data-testid="admin-media">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <p className="text-gray-500">Manage photos and videos</p>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-36 rounded-none" data-testid="filter-type-select">
                <SelectValue placeholder="All Media" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Media</SelectItem>
                <SelectItem value="photo">Photos</SelectItem>
                <SelectItem value="video">Videos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-media-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Media
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">Add New Media</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="media-form">
                <div>
                  <label className="text-sm font-medium">Title *</label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="rounded-none"
                    data-testid="media-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Type *</label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                    <SelectTrigger className="rounded-none" data-testid="media-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photo">Photo</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  {formData.type === "photo" ? (
                    <ImageUploader
                      value={formData.url}
                      onChange={(url) => setFormData({ ...formData, url })}
                      category="match-photo"
                      label="Photo *"
                      previewShape="wide"
                      testId="media-photo"
                    />
                  ) : (
                    <>
                      <label className="text-sm font-medium">Video URL *</label>
                      <Input
                        name="url"
                        value={formData.url}
                        onChange={handleChange}
                        required
                        className="rounded-none"
                        placeholder="YouTube / Vimeo / MP4 link"
                        data-testid="media-url-input"
                      />
                    </>
                  )}
                </div>
                {formData.type === 'video' && (
                  <ImageUploader
                    value={formData.thumbnail_url}
                    onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
                    category="match-photo"
                    label="Thumbnail"
                    previewShape="wide"
                    testId="media-thumbnail"
                  />
                )}
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="rounded-none"
                    rows={3}
                    data-testid="media-description-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Tags (comma separated)</label>
                  <Input
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="rounded-none"
                    placeholder="match, highlights, 2024"
                    data-testid="media-tags-input"
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="media-submit-btn">
                    Add Media
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="aspect-square bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filteredMedia.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredMedia.map(item => (
              <div key={item.media_id} className="bg-white border border-gray-200 overflow-hidden group relative" data-testid={`admin-media-card-${item.media_id}`}>
                <div className="aspect-square relative overflow-hidden">
                  {item.type === 'photo' ? (
                    <img 
                      src={item.url} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                      <Film className="w-12 h-12 text-white" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${
                      item.type === 'photo' ? 'bg-[#009E60] text-white' : 'bg-[#FFC72C] text-black'
                    }`}>
                      {item.type}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.media_id)}
                      className="rounded-none"
                      data-testid={`delete-media-${item.media_id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-sm uppercase text-[#09090b] truncate">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Media Yet</h3>
            <p className="text-gray-500 mb-4">Add your first photo or video to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
